import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Search, UserPlus, ArrowLeft, Loader2, Award } from "lucide-react";
import { toast } from "sonner";

/**
 * Public trainer directory + request flow.
 *
 * Search box drives `GET /trainers/directory?q=...`. Clicking "Send request"
 * opens a dialog where the client can attach a short message. Submission
 * fires `POST /trainers/request`. The trainer is notified by push + WS +
 * (if Resend configured) email.
 */
export default function TrainerDirectory() {
  const [q, setQ] = useState("");
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trainers/directory${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setTrainers(data || []);
    } catch {
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => { load(q); }, 280);
    return () => clearTimeout(t);
  }, [q]);

  const sendRequest = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await api.post("/trainers/request", {
        to_trainer_user_id: selected.user_id,
        message: message.trim() || null,
      });
      toast.success(`Request sent to ${selected.name}`);
      setSelected(null);
      setMessage("");
      load(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="trainer-directory" className="max-w-3xl mx-auto p-4 pb-24">
      <Link to="/profile" data-testid="back-to-profile-link" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white flex items-center gap-1 mb-1">
        <ArrowLeft className="w-3 h-3" /> Back to profile
      </Link>
      <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-[0.3em] text-blue-500 font-bold">
        <Award className="w-3 h-3" /> Find a Trainer
      </div>
      <h1 className="font-display text-4xl uppercase mb-4">Coach Directory</h1>

      <div className="relative mb-5">
        <Search className="w-4 h-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          data-testid="directory-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, specialty, or location…"
          className="pl-10 bg-neutral-900 border-neutral-800 text-white rounded-none h-12"
        />
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-neutral-400 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Searching…
        </div>
      ) : trainers.length === 0 ? (
        <div className="border border-neutral-800 p-8 text-center text-sm text-neutral-500">
          No trainers found. Try a different search, or invite one by email from your <Link to="/profile" className="text-blue-500">Profile</Link>.
        </div>
      ) : (
        <div className="space-y-2">
          {trainers.map((t) => (
            <div key={t.user_id} data-testid={`directory-card-${t.user_id}`} className="border border-neutral-800 bg-neutral-950 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl uppercase">{t.name}</div>
                  {t.trainer_location && <div className="text-xs text-neutral-400 mt-0.5">{t.trainer_location}</div>}
                  {t.trainer_bio && <p className="text-sm text-neutral-300 mt-2 leading-relaxed">{t.trainer_bio}</p>}
                  {t.trainer_specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {t.trainer_specialties.map((s, i) => (
                        <span key={i} className="text-[10px] uppercase tracking-widest text-blue-500 border border-blue-500/30 px-1.5 py-0.5">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  data-testid={`request-trainer-${t.user_id}`}
                  size="sm"
                  disabled={!t.trainer_accepting_clients}
                  onClick={() => setSelected(t)}
                  className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 shrink-0 disabled:opacity-40"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> Request
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setMessage(""); } }}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">Request {selected?.name}</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500 tracking-widest uppercase">
              Send a short note. They'll get a push notification and can accept or decline.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="request-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="Hey! I want to focus on powerlifting + mobility — looking for weekly check-ins. Cheers."
            className="bg-neutral-900 border-neutral-800 text-white rounded-none"
          />
          <Button
            data-testid="send-request-btn"
            onClick={sendRequest}
            disabled={sending}
            className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 mt-3"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Send Request
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
